import { createElement } from 'react';
import { FrontendTenantConfig, TenantAssets, TenantPalette, TenantSvgAssets } from './types';

/**
 * Unbranded fallbacks used ONLY when tenant resolution fails (unknown hostname, or D7 env
 * validation found missing keys).
 *
 * Why these exist at all: `theme.tsx` and the PDF stylesheets read tenant state at
 * module-evaluation time (D6), so `palette` / `resolvedTenantConfig` must always be defined —
 * they cannot be `null` the way `activeTenant` is.
 *
 * Why they are NEUTRAL rather than "the first registered tenant": the SPEC's compliance rule is
 * that on an unknown hostname "no tenant name, logo, asset URL or external id may render".
 * Falling back to a real tenant would make a boot-wiring bug render *plausibly* — WCS's hostname
 * quietly showing ISC's magenta — which is precisely the cross-tenant leak the rule forbids, and
 * it hides the bug instead of surfacing it. Falling back to grey leaks nothing and makes such a
 * bug immediately obvious to anyone looking at the screen.
 *
 * Greys are taken from the shared (tenant-agnostic) part of the palette, so nothing here is any
 * tenant's brand colour.
 */

const NEUTRAL_GREY = '#7E8288';
const NEUTRAL_GREY_LIGHT = '#F1F1F1';
const NEUTRAL_GREY_DARK = '#333333';

export const neutralPalette: TenantPalette = {
  primary: {
    light: NEUTRAL_GREY_LIGHT,
    main: NEUTRAL_GREY,
    dark: NEUTRAL_GREY_DARK,
    contrastText: '#FFFFFF',
  },
  primaryLight: {
    light: NEUTRAL_GREY_LIGHT,
    main: NEUTRAL_GREY_LIGHT,
    dark: NEUTRAL_GREY_LIGHT,
    contrastText: NEUTRAL_GREY,
  },
  secondary: {
    light: NEUTRAL_GREY_LIGHT,
    main: NEUTRAL_GREY,
  },
  pdfAccent: { main: NEUTRAL_GREY },
  pdfTint: { main: NEUTRAL_GREY_LIGHT },
  actionTint: { main: NEUTRAL_GREY_LIGHT },
  uiAccent: { main: NEUTRAL_GREY },
  textPrimary: { main: NEUTRAL_GREY_DARK },
  progressAccent: { main: NEUTRAL_GREY },
  progressTrack: { main: NEUTRAL_GREY_LIGHT },
  accordionIconOpen: { main: '#FFFFFF' },
};

/**
 * Identity fields are deliberately empty strings, not placeholder copy — an empty string renders
 * as nothing, whereas a placeholder ("Surrogacy Portal") is a name that could reach a user. The
 * PDF numbers are structural, not identifying, and a PDF is unreachable on the failure path
 * (export requires an authenticated session), so they carry inert defaults.
 */
export const neutralTenantConfig: FrontendTenantConfig = {
  slug: '',
  displayName: '',
  phone: { display: '', tel: '' },
  urls: {
    terms: '',
    privacy: '',
    requirements: '',
    contactUs: '',
    marketingBase: '',
  },
  pdf: {
    creator: '',
    producer: '',
    applicationCoverLogoWidth: 0,
    questionnaireCoverLogoWidth: 0,
  },
  head: {
    title: '',
    description: '',
  },
  // SAA-61/05 — empty arrays, not a real tenant's workflow: every gate reads `isEditableStatus()`,
  // which fails closed to "not editable" when `editableStatuses` is empty, matching the same
  // fail-closed rationale as the rest of this file.
  workflow: {
    statuses: [],
    editableStatuses: [],
    referralOptions: [],
    intendedParentTypes: [],
  },
  // SAA-84 — fail-closed: an unknown hostname never gets the Matching tool nav/route.
  matchingToolEnabled: false,
};

/**
 * Phase 3 (D5) — same rationale as `neutralPalette`/`neutralTenantConfig` above: `assets`/`svgAssets`
 * are read at module-evaluation time by consumers that cannot render conditionally (D6), so they
 * must always be defined. Empty-string URLs render nothing (no broken-image icon, no tenant asset
 * leaked) rather than falling back to a real tenant's logo/card images.
 */
export const neutralAssets: TenantAssets = {
  bgAuthMain: '',
  bgAuthMainSmall: '',
  logo: '',
  cardApplicationAboutYou: '',
  cardApplicationDeliveries: '',
  cardApplicationEmployment: '',
  cardApplicationHealth: '',
  cardApplicationPersonalHistory: '',
  cardApplicationPersonalInfo: '',
  cardApplicationPregnancyHistory: '',
  cardApplicationSignatureDisclosure: '',
  cardApplicationSurrogacyJourney: '',
  applicationPdfLogoMain: '',
  applicationPdfLogoSmallWhite: '',
  cardQuestionnaireAcknowledgement: '',
  cardQuestionnaireCoupleQuestions: '',
  cardQuestionnaireDefault: '',
  cardQuestionnaireFinal: '',
  cardQuestionnairePathToParenthood: '',
  cardQuestionnaireSecondIntendedParent: '',
  cardQuestionnaireSurrogacyJourney: '',
  cardQuestionnaireUploadPage: '',
  questionnairePdfLogoMain: '',
  authSignUpCard: '',
  authSignUpParentCard: '',
};

/** A blank SVG (no path, no brand hex) — renders as an empty icon rather than either tenant's
 *  `icon-lock.svg`. Built with `createElement` (not JSX) so this file can stay a plain `.ts` module. */
const NeutralLockIcon: TenantSvgAssets['lockIcon'] = ({ title, ...props }) =>
  createElement('svg', { viewBox: '0 0 24 24', ...props }, title ? createElement('title', null, title) : null);

export const neutralSvgAssets: TenantSvgAssets = {
  lockIcon: NeutralLockIcon,
};
